import { useAffiliationAvailabilityGate } from '@/hooks/use-affiliation-availability';
import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { Switch } from '@/components/ui/switch';
import { useUpdateProgramStatusAction } from '@/hooks/actions/use-update-program-status-action';
import { Flex } from '@chakra-ui/react';
import type { Program } from '@fluenceur/types';
import { ProgramStatus as ProgramState } from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProgramStatusItemProps {
  programId: string;
  isActive: boolean;
}

const ProgramStatus: React.FC<ProgramStatusItemProps> = ({ programId, isActive }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateStatus = useUpdateProgramStatusAction();
  const availability = useAffiliationAvailabilityGate();
  const [status, setStatus] = useState(isActive);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState(isActive);

  // Sync internal state when navigating between programs or when prop changes
  useEffect(() => {
    setStatus(isActive);
    setNewStatus(isActive);
    setIsDialogOpen(false);
  }, [programId, isActive]);

  const handleToggle = () => {
    const openConfirmation = () => {
      setNewStatus(!status);
      setIsDialogOpen(true);
    };
    if (status) openConfirmation();
    else availability.guard(openConfirmation);
  };

  const handleConfirm = async () => {
    try {
      await updateStatus.mutateAsync({ programId, isActive: newStatus });
      setStatus(newStatus);

      // Optimistically update cached lists and detail queries
      queryClient.setQueryData<Program[]>(
        ['programs'],
        (old) =>
          old?.map((p) =>
            p.id === programId
              ? {
                  ...p,
                  enabled: newStatus,
                  status: newStatus ? ProgramState.Active : ProgramState.Inactive,
                }
              : p
          ) ?? old
      );

      queryClient.setQueryData<Program>(['program', programId], (old) =>
        old
          ? {
              ...old,
              enabled: newStatus,
              status: newStatus ? ProgramState.Active : ProgramState.Inactive,
            }
          : old
      );
    } catch (error) {
      // A plan limit is answered by the upsell the API's 402 opened.
      console.error('Error while updating program status:', error);
    } finally {
      setIsDialogOpen(false);
    }
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
    setNewStatus(status);
  };

  return (
    <Flex align="center" justify="space-between">
      <Switch checked={status} onChange={handleToggle} colorPalette="primary">
        {status
          ? t('programs.programOverview.status.active')
          : t('programs.programOverview.status.inactive')}
      </Switch>
      {availability.modal}
      <ConfirmDialog
        isOpen={isDialogOpen}
        title={
          newStatus
            ? t('programs.programOverview.status.confirmEnableTitle')
            : t('programs.programOverview.status.confirmDisableTitle')
        }
        description={
          newStatus
            ? t('programs.programOverview.status.confirmEnableDescription')
            : t('programs.programOverview.status.confirmDisableDescription')
        }
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Flex>
  );
};

export default ProgramStatus;
